/**
 * The sessions beneath an activity, and where each has got to.
 *
 * This is the screen the 24 Aug refinement is really about. An activity used to
 * be one dated thing that either happened or did not; it is now a sequence of
 * units of work, each with somebody responsible for saying it is done. Staff
 * need to see, at a glance, which of those are outstanding and who is holding
 * them up.
 *
 * So the column that matters most is not the status — it is **who is awaited**.
 * "In progress" tells you nothing actionable; "waiting on the volunteer" tells
 * you who to ring.
 *
 * Sessions are shown as headings only when there is more than one. An activity
 * with a single session repeats its own name, and a heading that says what the
 * page already says is furniture.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  SelectInput,
  StatusBadge,
  TextArea,
} from '../../components/common';
import { useActivitySessions, useOverrideSession } from '../../hooks/useSessions';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../components/common';
import type { Session } from '../../services/api/sessions';
import { formatDuration } from '@parinaam-vms/shared';
import { formatDate, formatTimeRange, humaniseCode } from '../../utils/format';
import styles from './ActivitiesPage.module.css';

/**
 * Where an admin may move a session to.
 *
 * The whole lifecycle, not just COMPLETED. An override exists for the cases
 * the derived answer gets wrong, and "this was closed too early" is as real a
 * mistake as "this is finished and nobody signed it off" — so re-opening has
 * to be reachable, or the only way back is another kind of intervention
 * entirely.
 */
const OVERRIDE_CHOICES = [
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

/** A session over a range reads as a range; a one-day session reads as a day. */
function whenText(session: Session): string {
  if (!session.end_date || session.end_date === session.start_date)
    return formatDate(session.start_date);
  return `${formatDate(session.start_date)} – ${formatDate(session.end_date)}`;
}

/**
 * The actionable sentence.
 *
 * A completed session says who settled it, because "complete" invites the
 * question. An outstanding one names the side, not the state.
 */
function awaitingText(session: Session): string {
  if (session.status === 'COMPLETED') {
    return session.status_override ? 'Completed by an admin' : 'Complete';
  }
  switch (session.awaiting) {
    case 'BOTH':
      return 'Waiting on Parinaam and the volunteer';
    case 'PARINAAM':
      return 'Waiting on Parinaam';
    case 'VOLUNTEER':
      return 'Waiting on the volunteer';
    default:
      // Re-opened by an admin, with the confirmations cleared.
      return 'Not started';
  }
}

export function SessionPanel({ activityId }: { activityId: string }) {
  const sessions = useActivitySessions(activityId);
  const { hasRole } = useAuth();
  // Which row has its override form open, if any. One at a time: two open
  // forms on one screen is two reasons half-typed.
  const [overriding, setOverriding] = useState<string | null>(null);

  const mayOverride = hasRole('ADMIN');

  if (sessions.isPending) return <LoadingState label="Loading the sessions…" />;
  if (sessions.isError) {
    return (
      <Card title="Sessions">
        <ErrorState error={sessions.error} onRetry={() => void sessions.refetch()} />
      </Card>
    );
  }

  const rows = sessions.data ?? [];
  if (rows.length === 0) {
    return (
      <Card title="Sessions">
        <EmptyState
          title="No sessions yet"
          message="No sessions have been added yet."
        />
      </Card>
    );
  }

  const done = rows.filter((session) => session.status === 'COMPLETED').length;

  return (
    <Card
      title="Sessions"
      action={
        <span className={styles.sessionCount}>
          {done} of {rows.length} complete
        </span>
      }
    >
      <ol className={styles.sessionList}>
        {rows.map((session) => {
          return (
            <li key={session.session_id}>
              <div className={styles.sessionRow} data-status={session.status}>
                <div className={styles.sessionMain}>
                  {/*
                    The name is the link, not the whole row. A row carrying a
                    link AND an override button is two targets in one, and the
                    one people hit by accident is the one they did not mean.
                  */}
                  <p className={styles.sessionName}>
                    <Link to={`/sessions/${session.session_id}`}>{session.session_name}</Link>
                  </p>
                  <p className={styles.sessionMeta}>
                    {[
                      whenText(session),
                      (session.start_time || session.end_time) &&
                        formatTimeRange(session.start_time, session.end_time),
                      session.location,
                      session.planned_minutes != null &&
                        `${formatDuration(session.planned_minutes)} planned`,
                      session.assigned_count > 0 &&
                        `${session.assigned_count} ${session.assigned_count === 1 ? 'volunteer' : 'volunteers'}`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                  {session.description && (
                    <p className={styles.sessionMeta}>{session.description}</p>
                  )}
                  {session.status_override && session.override_reason && (
                    <p className={styles.sessionOverride}>Overridden: {session.override_reason}</p>
                  )}
                </div>

                <div className={styles.sessionState}>
                  <StatusBadge status={session.status} />
                  <span className={styles.sessionAwaiting}>{awaitingText(session)}</span>
                  {mayOverride && overriding !== session.session_id && (
                    <button
                      type="button"
                      className={styles.sessionOverrideLink}
                      onClick={() => setOverriding(session.session_id)}
                    >
                      Change status
                    </button>
                  )}
                </div>
              </div>

              {mayOverride && overriding === session.session_id && (
                <OverrideForm session={session} onClose={() => setOverriding(null)} />
              )}
            </li>
          );
        })}
      </ol>

      {/*
        Confirming is still not offered here — that is done by the party
        responsible, from their own screen, because a confirmation is a claim
        about who did something.

        Overriding is different, and is offered above: it is not a claim that
        the work was done, it is an admin's decision to set the status anyway,
        and it is recorded as exactly that.
      */}
      <p className={styles.sessionNote}>
        A session completes when whoever is responsible for it confirms, signed in to their own
        account.
        {mayOverride && ' An admin can set the status directly, with a reason.'}
      </p>
    </Card>
  );
}

/**
 * An admin setting a session's status by hand.
 *
 * Inline under the row rather than in a modal. The decision needs the row for
 * context — which session, who is awaited, what the dates were — and a dialog
 * that covers it makes the admin remember what they were looking at.
 *
 * The reason is mandatory here, in the request schema and in a database CHECK.
 * Three layers for one field is not belt-and-braces: this is the only
 * backwards status move in the system, and the reason is the entire account of
 * why the derived answer was overruled.
 */
function OverrideForm({ session, onClose }: { session: Session; onClose: () => void }) {
  const override = useOverrideSession();
  const toast = useToast();
  const [status, setStatus] = useState<Session['status']>('COMPLETED');
  const [reason, setReason] = useState('');

  const ready = reason.trim().length > 0 && status !== session.status;

  const submit = () => {
    if (!ready) return;
    override.mutate(
      { sessionId: session.session_id, status, reason: reason.trim() },
      {
        onSuccess: () => {
          toast.success(`${session.session_name} is now ${humaniseCode(status)}.`);
          onClose();
        },
        onError: (error) => toast.error(error, 'The status could not be changed.'),
      }
    );
  };

  return (
    <div className={styles.sessionOverrideForm}>
      <SelectInput
        label="Set the status to"
        options={OVERRIDE_CHOICES}
        value={status}
        onChange={(event) => setStatus(event.target.value as Session['status'])}
        disabled={override.isPending}
      />
      <TextArea
        label="Why"
        required
        rows={2}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        disabled={override.isPending}
      />
      <div className={styles.sessionOverrideActions}>
        <Button variant="secondary" onClick={onClose} disabled={override.isPending}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={submit}
          disabled={!ready || override.isPending}
          loading={override.isPending}
        >
          Change status
        </Button>
      </div>
      {status === session.status && (
        <p className={styles.sessionOverrideHint}>That is the status it already has.</p>
      )}
    </div>
  );
}
